import {
  CloseOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  PlusOutlined
} from "@ant-design/icons";
import { Button, Input, InputNumber, Modal, Select, Tooltip } from "antd";
import { useEffect, useMemo, useState } from "react";
import { DatePicker } from "components/antd-date-picker";
import type { Dayjs } from "dayjs";
import {
  DATE_FORMAT_MMDDYYYY,
  DATE_FORMAT_YYYYMMDD,
  DateUtils
} from "utils/dateutils";
import { ErrorMessages } from "../../constants";
import ConditionalFormattingStylePicker from "./conditional-formatting-style-picker";
import { DEFAULT_CONDITIONAL_FORMATTING_ACTIONS } from "./normalize-rules";
import { CONDITIONAL_FORMATTING_PRESET_STYLES } from "./preset-styles";
import type {
  ConditionalFormattingActions,
  ConditionalFormattingCondition,
  ConditionalFormattingConditionGroup,
  ConditionalFormattingFilterType,
  ConditionalFormattingRule
} from "./types";

const { RangePicker } = DatePicker;

export type ConditionalFormattingColumnOption = {
  id: string;
  label: string;
  filterType: ConditionalFormattingFilterType;
  options: string[];
  selectable?: boolean;
};

const CONDITION_OPTIONS: Record<
  ConditionalFormattingFilterType,
  Array<{ value: string; label: string }>
> = {
  set: [
    { value: "equals", label: "is" },
    { value: "notEqual", label: "is not" },
    { value: "blank", label: "is empty" },
    { value: "notBlank", label: "is not empty" }
  ],
  text: [
    { value: "equals", label: "is" },
    { value: "notEqual", label: "is not" },
    { value: "contains", label: "contains" },
    { value: "notContains", label: "does not contain" },
    { value: "blank", label: "is empty" },
    { value: "notBlank", label: "is not empty" }
  ],
  date: [
    { value: "equals", label: "is" },
    { value: "notEqual", label: "is not" },
    { value: "lessThan", label: "is before" },
    { value: "greaterThan", label: "is after" },
    { value: "blank", label: "is empty" },
    { value: "notBlank", label: "is not empty" },
    { value: "inRange", label: "range" }
  ],
  number: [
    { value: "equals", label: "equals" },
    { value: "notEqual", label: "does not equal" },
    { value: "greaterThan", label: "greater than" },
    { value: "lessThan", label: "less than" },
    { value: "inRange", label: "is between" },
    { value: "notInRange", label: "is not between" },
    { value: "blank", label: "is empty" },
    { value: "notBlank", label: "is not empty" }
  ]
};

const hasNoValue = (type: string) => type === "blank" || type === "notBlank";
const isRange = (type: string) => type === "inRange" || type === "notInRange";

const createCondition = (
  filterType: ConditionalFormattingFilterType
): ConditionalFormattingCondition => ({ filterType, type: "" });

const createConditionGroup = (
  filterType: ConditionalFormattingFilterType
): ConditionalFormattingConditionGroup => ({
  filterType,
  conditions: [createCondition(filterType)],
  actions: { ...CONDITIONAL_FORMATTING_PRESET_STYLES[0] }
});

const createColumnFormatting = (
  column?: ConditionalFormattingColumnOption
): ConditionalFormattingRule => ({
  columnId: column?.id ?? "",
  defaultActions: { ...DEFAULT_CONDITIONAL_FORMATTING_ACTIONS },
  conditions: [createConditionGroup(column?.filterType ?? "text")]
});

const cloneColumnFormatting = (
  value: ConditionalFormattingRule
): ConditionalFormattingRule => ({
  columnId: value.columnId,
  defaultActions: { ...value.defaultActions },
  conditions: value.conditions.map((group) => ({
    ...group,
    conditions: group.conditions.map((condition) => ({ ...condition })),
    actions: { ...group.actions }
  }))
});

const ACTION_KEYS: Array<keyof ConditionalFormattingActions> = [
  "backgroundColor",
  "textColor",
  "fontSize",
  "bold",
  "italic",
  "underline",
  "strike"
];

const hasConditionValue = (condition: ConditionalFormattingCondition) => {
  if (!condition.type) return false;
  if (hasNoValue(condition.type)) return true;
  if (condition.filterType === "set") return !!condition.values?.length;
  if (condition.filterType === "date") {
    return isRange(condition.type)
      ? !!condition.dateFrom && !!condition.dateTo
      : !!condition.dateFrom;
  }
  if (isRange(condition.type)) {
    return (
      condition.filter !== null &&
      condition.filter !== undefined &&
      condition.filter !== "" &&
      condition.filterTo !== null &&
      condition.filterTo !== undefined &&
      condition.filterTo !== ""
    );
  }
  return (
    condition.filter !== null &&
    condition.filter !== undefined &&
    condition.filter !== ""
  );
};

const isConditionGroupComplete = (group: ConditionalFormattingConditionGroup) =>
  group.conditions.length > 0 && group.conditions.every(hasConditionValue);

const hasIncompleteConditions = (rule: ConditionalFormattingRule) =>
  rule.conditions.some((group) => !isConditionGroupComplete(group));

const isSameActions = (
  left: ConditionalFormattingActions,
  right: ConditionalFormattingActions
) =>
  ACTION_KEYS.every(
    (key) => (left[key] ?? undefined) === (right[key] ?? undefined)
  );

const isSameCondition = (
  left: ConditionalFormattingCondition,
  right: ConditionalFormattingCondition
) =>
  left.filterType === right.filterType &&
  left.type === right.type &&
  left.filter === right.filter &&
  left.filterTo === right.filterTo &&
  left.dateFrom === right.dateFrom &&
  left.dateTo === right.dateTo &&
  JSON.stringify(left.values ?? []) === JSON.stringify(right.values ?? []);

const isSameRule = (
  left: ConditionalFormattingRule,
  right: ConditionalFormattingRule
) =>
  left.columnId === right.columnId &&
  isSameActions(left.defaultActions, right.defaultActions) &&
  left.conditions.length === right.conditions.length &&
  left.conditions.every((group, index) => {
    const other = right.conditions[index];
    return (
      group.filterType === other.filterType &&
      isSameActions(group.actions, other.actions) &&
      group.conditions.length === other.conditions.length &&
      group.conditions.every((condition, conditionIndex) =>
        isSameCondition(condition, other.conditions[conditionIndex])
      )
    );
  });

const TEXT_STYLE_FLAGS: Array<"bold" | "italic" | "underline" | "strike"> = [
  "bold",
  "italic",
  "underline",
  "strike"
];

const normalizeColor = (color?: string) =>
  String(color ?? "")
    .trim()
    .toLowerCase();

const isDefaultTextColor = (color?: string) => {
  const value = normalizeColor(color);
  return (
    !value ||
    value.startsWith("var(") ||
    value === "#262626" ||
    value === "#000000" ||
    value === "black"
  );
};

const isDefaultFillColor = (color?: string) => {
  const value = normalizeColor(color);
  return (
    !value ||
    value.startsWith("var(") ||
    value === "transparent" ||
    value === "#ffffff" ||
    value === "#fff" ||
    value === "white"
  );
};

const isEffectivelyDefaultFormatting = (
  actions: ConditionalFormattingActions
) =>
  isDefaultTextColor(actions.textColor) &&
  isDefaultFillColor(actions.backgroundColor) &&
  !actions.fontSize &&
  TEXT_STYLE_FLAGS.every((key) => !actions[key]);

const toSavableColumnFormatting = (rule: ConditionalFormattingRule) =>
  cloneColumnFormatting({
    ...rule,
    conditions: rule.conditions.filter((group) =>
      group.conditions.some(hasConditionValue)
    )
  });

// Do not run picker Dayjs values through DateUtils.format � that applies
// .tz() (project zone) and rolls a naive local midnight back a day when the
// machine sits east of the project (e.g. IST vs EDT).
const toDatePickerValue = (date?: string | null) => {
  if (!date) return null;
  const parsed = DateUtils.dateTimeObj(date, DATE_FORMAT_YYYYMMDD);
  return parsed.isValid() ? parsed : null;
};

const toStoredCalendarDate = (date: Dayjs | null) =>
  date?.isValid() ? date.format(DATE_FORMAT_YYYYMMDD) : null;

const isClearFormattingSave = (rule: ConditionalFormattingRule) =>
  rule.conditions.length === 0 &&
  isEffectivelyDefaultFormatting(rule.defaultActions);

type Props = {
  open: boolean;
  columns: ConditionalFormattingColumnOption[];
  formattedColumnIds?: string[];
  readOnly?: boolean;
  value?: ConditionalFormattingRule;
  initialColumnId?: string;
  onCancel: () => void;
  onClear?: () => void;
  onSave: (value: ConditionalFormattingRule) => void;
};

export default function SetupConditionalFormattingPopup({
  open,
  columns,
  formattedColumnIds = [],
  readOnly = false,
  value,
  initialColumnId,
  onCancel,
  onClear,
  onSave
}: Props) {
  const initialColumn = columns.find(
    (column) => column.id === (value?.columnId ?? initialColumnId)
  );
  const [draft, setDraft] = useState<ConditionalFormattingRule>(() =>
    value ? cloneColumnFormatting(value) : createColumnFormatting(initialColumn)
  );
  const [draggedGroupIndex, setDraggedGroupIndex] = useState<number | null>(
    null
  );
  const [dragOverGroupIndex, setDragOverGroupIndex] = useState<number | null>(
    null
  );

  useEffect(() => {
    if (!open) return;
    const column = columns.find(
      (item) => item.id === (value?.columnId ?? initialColumnId)
    );
    setDraft(
      value ? cloneColumnFormatting(value) : createColumnFormatting(column)
    );
    setDraggedGroupIndex(null);
    setDragOverGroupIndex(null);
  }, [columns, initialColumnId, open, value]);

  const selectedColumn = columns.find((column) => column.id === draft.columnId);
  const availableColumns = columns.filter(
    (column) =>
      column.id === value?.columnId ||
      (column.selectable !== false && !formattedColumnIds.includes(column.id))
  );
  const lockColumn = !!value || !!initialColumnId;
  const baseline = useMemo(() => {
    const column = columns.find(
      (item) => item.id === (value?.columnId ?? initialColumnId)
    );
    return value
      ? cloneColumnFormatting(value)
      : createColumnFormatting(column);
  }, [columns, initialColumnId, value]);
  const canSave = useMemo(
    () =>
      !!draft.columnId &&
      !hasIncompleteConditions(draft) &&
      !isSameRule(draft, baseline),
    [baseline, draft]
  );

  const saveDisabledTitle = useMemo(() => {
    if (readOnly) return ErrorMessages.PermissionNotGranted;
    if (canSave) return "";
    if (!draft.columnId) return "Please select a column.";
    if (hasIncompleteConditions(draft)) {
      return "Complete or remove any unfinished conditions to Save.";
    }
    return "No changes to Save.";
  }, [canSave, draft, readOnly]);

  const updateGroup = (
    groupIndex: number,
    update: (
      group: ConditionalFormattingConditionGroup
    ) => ConditionalFormattingConditionGroup
  ) =>
    setDraft((current) => ({
      ...current,
      conditions: current.conditions.map((group, index) =>
        index === groupIndex ? update(group) : group
      )
    }));

  const updateCondition = (
    groupIndex: number,
    conditionIndex: number,
    updates: Partial<ConditionalFormattingCondition>
  ) =>
    updateGroup(groupIndex, (group) => ({
      ...group,
      conditions: group.conditions.map((condition, index) =>
        index === conditionIndex ? { ...condition, ...updates } : condition
      )
    }));

  const removeCondition = (groupIndex: number, conditionIndex: number) =>
    updateGroup(groupIndex, (group) => ({
      ...group,
      conditions: group.conditions.filter(
        (_, index) => index !== conditionIndex
      )
    }));

  const removeConditionGroup = (groupIndex: number) =>
    setDraft((current) => ({
      ...current,
      conditions: current.conditions.filter((_, index) => index !== groupIndex)
    }));

  const moveConditionGroup = (targetIndex: number) => {
    if (
      draggedGroupIndex === null ||
      draggedGroupIndex === targetIndex ||
      readOnly
    ) {
      setDraggedGroupIndex(null);
      setDragOverGroupIndex(null);
      return;
    }
    setDraft((current) => {
      const conditions = [...current.conditions];
      const [movedCondition] = conditions.splice(draggedGroupIndex, 1);
      conditions.splice(targetIndex, 0, movedCondition);
      return { ...current, conditions };
    });
    setDraggedGroupIndex(null);
    setDragOverGroupIndex(null);
  };

  const renderValueInput = (
    condition: ConditionalFormattingCondition,
    groupIndex: number,
    conditionIndex: number
  ) => {
    if (!condition.type || hasNoValue(condition.type)) return null;
    const update = (updates: Partial<ConditionalFormattingCondition>) =>
      updateCondition(groupIndex, conditionIndex, updates);

    if (condition.filterType === "set") {
      return (
        <Select
          mode="multiple"
          disabled={readOnly}
          value={condition.values ?? []}
          placeholder="Select values"
          showSearch
          className="w-full"
          options={(selectedColumn?.options ?? []).map((option) => ({
            value: option,
            label: option
          }))}
          onChange={(values) => update({ values })}
        />
      );
    }
    if (condition.filterType === "date") {
      if (isRange(condition.type)) {
        return (
          <RangePicker
            disabled={readOnly}
            className="w-full"
            format={DATE_FORMAT_MMDDYYYY}
            value={[
              toDatePickerValue(condition.dateFrom),
              toDatePickerValue(condition.dateTo)
            ]}
            onChange={(dates) =>
              update({
                dateFrom: toStoredCalendarDate(dates?.[0] ?? null),
                dateTo: toStoredCalendarDate(dates?.[1] ?? null)
              })
            }
          />
        );
      }
      return (
        <DatePicker
          disabled={readOnly}
          className="w-full"
          format={DATE_FORMAT_MMDDYYYY}
          placeholder="Select date"
          value={toDatePickerValue(condition.dateFrom)}
          onChange={(date) => update({ dateFrom: toStoredCalendarDate(date) })}
        />
      );
    }
    if (condition.filterType === "number") {
      return (
        <div className="flex w-[220px] gap-2">
          <InputNumber
            disabled={readOnly}
            aria-label={isRange(condition.type) ? "Minimum value" : "Value"}
            className="w-full"
            value={
              typeof condition.filter === "number"
                ? condition.filter
                : undefined
            }
            onChange={(filter) => update({ filter })}
          />
          {isRange(condition.type) ? (
            <InputNumber
              disabled={readOnly}
              aria-label="Maximum value"
              className="w-full"
              value={
                typeof condition.filterTo === "number"
                  ? condition.filterTo
                  : undefined
              }
              onChange={(filterTo) => update({ filterTo })}
            />
          ) : null}
        </div>
      );
    }
    return (
      <Input
        disabled={readOnly}
        aria-label="Condition value"
        className="w-full"
        value={String(condition.filter ?? "")}
        onChange={(event) => update({ filter: event.target.value })}
      />
    );
  };

  return (
    <Modal
      title="Set Up Conditional Formatting"
      open={open}
      width={760}
      className="ag-theme-alpine"
      destroyOnHidden
      onCancel={onCancel}
      footer={[
        <div className="flex items-center justify-end px-2" key="footer">
          <Tooltip
            key="clear"
            title={readOnly ? ErrorMessages.PermissionNotGranted : ""}
          >
            <span className="pr-2">
              <Button type="text" danger disabled={readOnly} onClick={onClear}>
                Clear Formatting
              </Button>
            </span>
          </Tooltip>
          <Button key="cancel" onClick={onCancel}>
            Cancel
          </Button>
          <Tooltip key="save" title={saveDisabledTitle}>
            <span className="pl-2">
              <Button
                type="primary"
                disabled={!canSave || readOnly}
                onClick={() => {
                  const toSave = toSavableColumnFormatting(draft);
                  if (isClearFormattingSave(toSave)) {
                    onClear?.();
                    return;
                  }
                  onSave(toSave);
                }}
              >
                Save
              </Button>
            </span>
          </Tooltip>
        </div>
      ]}
    >
      <div className="flex items-center gap-5 py-1">
        <div className="text-black text-opacity-90">
          Apply to column:{" "}
          {lockColumn ? (
            <strong>{selectedColumn?.label ?? draft.columnId}</strong>
          ) : (
            <Select
              disabled={readOnly}
              value={draft.columnId || undefined}
              placeholder="Select column"
              showSearch
              optionFilterProp="label"
              className="w-56"
              options={availableColumns.map((column) => ({
                value: column.id,
                label: column.label
              }))}
              onChange={(columnId) => {
                const changeColumn = () => {
                  const column = columns.find((item) => item.id === columnId);
                  setDraft(createColumnFormatting(column));
                };
                if (
                  draft.columnId &&
                  draft.columnId !== columnId &&
                  draft.conditions.length
                ) {
                  Modal.confirm({
                    title: "Change column?",
                    icon: <ExclamationCircleOutlined />,
                    content:
                      "Changing the column will result in the conditions being deleted. Are you sure you want to proceed?",
                    okText: "Proceed",
                    onOk: changeColumn
                  });
                  return;
                }
                changeColumn();
              }}
            />
          )}
        </div>
        <div className="h-10 border-0 border-l border-solid border-ciq-gray-300" />
        <div className="flex items-center gap-2 text-black text-opacity-90">
          Default Formatting style
          <ConditionalFormattingStylePicker
            disabled={readOnly}
            value={draft.defaultActions}
            onChange={(defaultActions) =>
              setDraft((current) => ({ ...current, defaultActions }))
            }
          />
        </div>
      </div>

      <div className="py-2">
        <div className="mb-1 font-medium text-black text-opacity-90">
          Conditions List
        </div>
        <div className="mb-5 text-xs text-black text-opacity-45">
          <InfoCircleOutlined className="text-ciq-gray-300 pr-1" />
          If multiple conditions match a cell, the top-most condition in the
          list will be applied.
        </div>

        <div className="flex flex-col gap-3 text-xs">
          {draft.conditions.map((group, groupIndex) => (
            <div
              key={`condition-group-${groupIndex}`}
              className="flex w-full items-center"
            >
              <span className="font-medium w-6">{groupIndex + 1}.</span>
              <div
                className={`flex grow items-center gap-3 bg-black bg-opacity-5 border-solid border-0 border-l-2 border-black border-opacity-80 ${
                  dragOverGroupIndex === groupIndex
                    ? "outline outline-2 outline-ciq-primary-900"
                    : ""
                }`}
                onDragOver={(event) => {
                  if (draggedGroupIndex === null || readOnly) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "move";
                  setDragOverGroupIndex(groupIndex);
                }}
                onDragLeave={() =>
                  setDragOverGroupIndex((current) =>
                    current === groupIndex ? null : current
                  )
                }
                onDrop={(event) => {
                  event.preventDefault();
                  moveConditionGroup(groupIndex);
                }}
              >
                <span
                  className={`px-2 ag-icon ag-icon-grip ${
                    readOnly ? "cursor-not-allowed" : "cursor-grab"
                  }`}
                  draggable={!readOnly}
                  aria-label={`Drag condition ${groupIndex + 1}`}
                  onDragStart={(event) => {
                    setDraggedGroupIndex(groupIndex);
                    setDragOverGroupIndex(groupIndex);
                    event.dataTransfer.effectAllowed = "move";
                    event.dataTransfer.setData(
                      "text/plain",
                      groupIndex.toString()
                    );
                  }}
                  onDragEnd={() => {
                    setDraggedGroupIndex(null);
                    setDragOverGroupIndex(null);
                  }}
                />

                <div className="min-w-0 flex-1">
                  {group.conditions.map((condition, conditionIndex) => (
                    <div
                      key={`condition-${groupIndex}-${conditionIndex}`}
                      className="mb-2 flex items-center gap-2 last:mb-0 py-1"
                    >
                      <span className="text-black text-opacity-90 pl-1 w-20 shrink-0">
                        When Value
                      </span>
                      <Select
                        disabled={readOnly}
                        aria-label={`Condition ${groupIndex + 1} type`}
                        value={condition.type || undefined}
                        placeholder="Select Condition"
                        popupMatchSelectWidth={false}
                        options={CONDITION_OPTIONS[group.filterType]}
                        onChange={(type) =>
                          updateCondition(groupIndex, conditionIndex, {
                            type,
                            filter: undefined,
                            filterTo: undefined,
                            dateFrom: undefined,
                            dateTo: undefined,
                            values: undefined
                          })
                        }
                      />
                      {renderValueInput(condition, groupIndex, conditionIndex)}
                      {conditionIndex > 0 ? (
                        <Button
                          type="text"
                          danger
                          disabled={readOnly}
                          icon={<CloseOutlined />}
                          aria-label="Remove nested condition"
                          onClick={() =>
                            removeCondition(groupIndex, conditionIndex)
                          }
                        />
                      ) : null}
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-black text-opacity-90">
                    Formatting style
                  </span>
                  <ConditionalFormattingStylePicker
                    disabled={readOnly}
                    value={group.actions}
                    onChange={(actions) =>
                      updateGroup(groupIndex, (current) => ({
                        ...current,
                        actions
                      }))
                    }
                  />
                  <Button
                    type="text"
                    danger
                    disabled={readOnly}
                    aria-label={`Remove condition ${groupIndex + 1}`}
                    icon={
                      <CloseOutlined className="text-black text-opacity-45 hover:text-black hover:text-opacity-50" />
                    }
                    onClick={() => removeConditionGroup(groupIndex)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <Tooltip title={readOnly ? ErrorMessages.PermissionNotGranted : ""}>
          <span>
            <Button
              type="link"
              icon={<PlusOutlined />}
              className="mt-3 !px-0 text-ciq-primary-900 hover:!text-ciq-primary-900"
              disabled={!selectedColumn || readOnly}
              onClick={() =>
                setDraft((current) => ({
                  ...current,
                  conditions: [
                    ...current.conditions,
                    createConditionGroup(selectedColumn?.filterType ?? "text")
                  ]
                }))
              }
            >
              Add Condition
            </Button>
          </span>
        </Tooltip>
      </div>
    </Modal>
  );
}
